import React from 'react';
import { Box, Grid, InputAdornment, MenuItem, Typography } from '@mui/material';
import {
  Badge,
  Email,
  Gavel,
  Handshake,
  Link as LinkIcon,
  Person,
  Phone,
  Share,
  Storefront,
  Work,
} from '@mui/icons-material';
import EducationSection from './EducationSection';
import DetailField from './DetailField';
import { COMPANY_SECTIONS, PERSON_SECTIONS, RELATIONSHIP_SECTION } from './fieldConfig';

export * from './fieldConfig';
export { EducationSection, DetailField };

const SECTION_ICONS = {
  badge: Badge,
  work: Work,
  share: Share,
  gavel: Gavel,
  storefront: Storefront,
  person: Person,
  handshake: Handshake,
};

const FIELD_ICONS = {
  email: Email,
  phone: Phone,
  url: LinkIcon,
};

const ExtraField = ({ field, value, error, onChange, readOnly }) => {
  const Icon = FIELD_ICONS[field.type];
  const isSelect = field.type === 'select';
  const isUpperCase = field.type === 'gst' || field.type === 'pan';

  const handleChange = (event) => {
    let next = event.target.value;
    if (isUpperCase) next = next.toUpperCase();
    if (field.type === 'year') next = next.replace(/\D/g, '').slice(0, 4);
    if (field.type === 'number') next = next.replace(/\D/g, '');
    onChange(field.name, next);
  };

  return (
    <DetailField
      fullWidth
      readOnly={readOnly}
      link={field.type === 'url'}
      select={isSelect}
      type={field.type === 'date' ? 'date' : 'text'}
      multiline={field.type === 'multiline'}
      minRows={field.type === 'multiline' ? 3 : undefined}
      label={field.label}
      value={value ?? ''}
      onChange={handleChange}
      error={!!error}
      helperText={error || field.helperText}
      placeholder={field.placeholder}
      InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
      inputProps={{
        ...(field.type === 'date' ? { max: new Date().toISOString().slice(0, 10) } : {}),
        ...(field.type === 'year' || field.type === 'number' ? { inputMode: 'numeric' } : {}),
      }}
      InputProps={
        Icon
          ? {
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }
          : undefined
      }
    >
      {isSelect && [
        <MenuItem key="" value="">
          <em>Not set</em>
        </MenuItem>,
        ...field.options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        )),
      ]}
    </DetailField>
  );
};

const ExtraSection = ({ section, formData, errors, onFieldChange, readOnly }) => {
  const Icon = SECTION_ICONS[section.icon] || Badge;
  return (
    <Box sx={{ mb: { xs: 3, sm: 4 } }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
      >
        <Icon sx={{ mr: 1, color: '#3b82f6', fontSize: { xs: 20, sm: 24 } }} />
        {section.title}
      </Typography>
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        {section.fields.map((field) => (
          <Grid item xs={12} md={field.md || 6} key={field.name}>
            <ExtraField
              field={field}
              value={formData[field.name]}
              error={errors[field.name]}
              onChange={onFieldChange}
              readOnly={readOnly}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Renders the extra sections for the given contact type: profile sections, education (people only), relationship
export const ContactExtraSections = ({ contactType, formData, errors = {}, onFieldChange, readOnly = false }) => {
  const sectionProps = { formData, errors, onFieldChange, readOnly };
  const profileSections = contactType === 'company' ? COMPANY_SECTIONS : PERSON_SECTIONS;

  return (
    <Box>
      {profileSections.map((section) => (
        <ExtraSection key={section.key} section={section} {...sectionProps} />
      ))}

      {contactType === 'person' && (
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <EducationSection
            value={formData.education || []}
            onChange={(education) => onFieldChange('education', education)}
            errors={errors}
            readOnly={readOnly}
          />
        </Box>
      )}

      <ExtraSection section={RELATIONSHIP_SECTION} {...sectionProps} />
    </Box>
  );
};

export default ContactExtraSections;
